import { useEffect, useState } from "react";
import { getDoc, doc, getFirestore } from "firebase/firestore";
import { firebaseApp } from "../../firebase";

export default function SGloopSubMainCard({ userData }) {
  const [sgData, setSgData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3; // adjust as needed
  const db = getFirestore(firebaseApp);

  useEffect(() => {
    if (!userData?.oneSGGroupRef || userData.oneSGGroupRef.length === 0) {
      setSgData([]);
      setLoading(false);
      return;
    }

    const fetchAllGroups = async () => {
      try {
        const results = await Promise.all(
          userData.oneSGGroupRef.map(async (ref) => {
            if (!ref.path) return null;
            const docRef = doc(db, ref.path);
            const docSnap = await getDoc(docRef);
            return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
          })
        );

        setSgData(results.filter(Boolean));
      } catch (err) {
        console.error("Error fetching oneSG groups:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAllGroups();
  }, [userData]);

  const totalPages = Math.ceil(sgData.length / itemsPerPage);
  const paginatedData = sgData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const goToPage = (pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
    }
  };

  if (loading) {
    return (
      <div className="p-4 bg-white rounded-xl shadow-md text-center">
        <div className="text-gray-500 text-sm">Loading SG data...</div>
      </div>
    );
  }

  if (sgData.length === 0) {
    return (
      <div className="p-4 bg-white rounded-xl shadow-md text-center">
        <div className="text-gray-500 text-sm">No SG groups found</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* SG Cards */}
      {paginatedData.map((data) => (
        <div
          key={data.id}
          className="p-6 bg-white rounded-2xl shadow-md space-y-3"
        >
          {/* Name */}
          <h2 className="text-base font-bold text-gray-800">{data.subGroupName}</h2>

          {/* Number of pax */}
          <div className="text-gray-600 text-sm">
            <span className="font-medium">
              {data?.subMembershipSubscription?.length > 0
                ? data.subMembershipSubscription.length
                : 0}
            </span>{" "}
            members inside
          </div>

          {/* Upcoming events */}
          <div className="mt-3">
            <h3 className="text-sm font-semibold text-gray-700">
              Upcoming Events
            </h3>
            <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
              {data.upcomingEvents?.length > 0 ? (
                data.upcomingEvents.slice(0, 3).map((event, i) => (
                  <li key={i}>
                    {event.eventName}{" "}
                    <span className="text-gray-400 text-xs">
                      ({event.timestamp?.toDate
                        ? event.timestamp.toDate().toLocaleDateString()
                        : event.timestamp})
                    </span>
                  </li>
                ))
              ) : (
                <li>No upcoming events</li>
              )}
            </ul>
          </div>
        </div>
      ))}

      {/* Pagination Controls */}
      <div className="flex justify-center items-center space-x-2 mt-4">
        <button
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-3 py-1 rounded-md text-sm font-medium ${currentPage === 1
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : "bg-gray-800 text-white hover:bg-gray-700"
            }`}
        >
          Prev
        </button>

        <span className="text-sm text-gray-600">
          Page {currentPage} of {totalPages}
        </span>

        <button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`px-3 py-1 rounded-md text-sm font-medium ${currentPage === totalPages
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : "bg-gray-800 text-white hover:bg-gray-700"
            }`}
        >
          Next
        </button>
      </div>
    </div>
  );
}
